import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { ArrowDownLeft, ArrowUpRight, ChevronRight, Loader2, Mic, Music } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_VARIANT = {
  queued: "outline",
  leased: "secondary",
  dialing: "secondary",
  in_progress: "default",
  completed: "default",
  busy: "outline",
  no_answer: "outline",
  voicemail: "secondary",
  failed: "destructive",
  cancelled: "destructive",
};

function formatPhone(p) {
  try {
    const n = parsePhoneNumberFromString(p);
    return n ? n.formatInternational() : p;
  } catch {
    return p;
  }
}

function durationStr(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

export default function Calls() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 50;
  const [filters, setFilters] = useState({
    status: "__all__",
    direction: "__all__",
    agent_id: "__all__",
    campaign_id: "__all__",
    trunk_id: "__all__",
    date_from: "",
    date_to: "",
    phone: "",
  });
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [trunks, setTrunks] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/agents"),
      api.get("/campaigns"),
      api.get("/trunks"),
    ]).then(([a, c, t]) => {
      setAgents(a.data);
      setCampaigns(c.data);
      setTrunks(t.data);
    }).catch((err) => toastError(err, "Failed to load filter data"));
  }, []);

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const params = { limit, offset: page * limit };
      for (const [k, v] of Object.entries(filters)) {
        if (v && v !== "__all__") params[k] = v;
      }
      const r = await api.get("/calls", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      toastError(err, "Failed to load calls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  return (
    <div className="space-y-6" data-testid="calls-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Calls</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every leased / completed / inbound call. Click a row to drill into transcript, recording and latency.
        </p>
      </div>

      {/* Filter bar */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 rounded-lg border border-border bg-card p-3">
        <Filter label="Status" value={filters.status} options={[
          { value: "__all__", label: "All" },
          ...Object.keys(STATUS_VARIANT).map(v => ({ value: v, label: v }))
        ]} onChange={(v) => { setFilters((f) => ({ ...f, status: v })); setPage(0); }} testid="calls-filter-status" />
        <Filter label="Direction" value={filters.direction} options={[
          { value: "__all__", label: "All" },
          { value: "outbound", label: "Outbound" },
          { value: "inbound", label: "Inbound" },
        ]} onChange={(v) => { setFilters((f) => ({ ...f, direction: v })); setPage(0); }} testid="calls-filter-direction" />
        <Filter label="Agent" value={filters.agent_id} options={[
          { value: "__all__", label: "All agents" },
          ...agents.map(a => ({ value: a.id, label: a.name })),
        ]} onChange={(v) => { setFilters((f) => ({ ...f, agent_id: v })); setPage(0); }} testid="calls-filter-agent" />
        <Filter label="Campaign" value={filters.campaign_id} options={[
          { value: "__all__", label: "All campaigns" },
          ...campaigns.map(c => ({ value: c.id, label: c.name })),
        ]} onChange={(v) => { setFilters((f) => ({ ...f, campaign_id: v })); setPage(0); }} testid="calls-filter-campaign" />
        <Filter label="Trunk" value={filters.trunk_id} options={[
          { value: "__all__", label: "All trunks" },
          ...trunks.map(t => ({ value: t.id, label: t.name })),
        ]} onChange={(v) => { setFilters((f) => ({ ...f, trunk_id: v })); setPage(0); }} testid="calls-filter-trunk" />
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Phone</Label>
          <Input
            value={filters.phone}
            placeholder="+1202…"
            onChange={(e) => { setFilters((f) => ({ ...f, phone: e.target.value })); setPage(0); }}
            data-testid="calls-filter-phone"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input
            type="datetime-local" value={filters.date_from}
            onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(0); }}
            data-testid="calls-filter-from"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input
            type="datetime-local" value={filters.date_to}
            onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(0); }}
            data-testid="calls-filter-to"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center" data-testid="calls-empty">
            <div className="rounded-full bg-secondary p-3 inline-block">
              <Music className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="mt-3 text-base font-medium">No calls yet</div>
            <p className="mt-1 text-sm text-muted-foreground">
              Launch a campaign or receive an inbound call.
            </p>
          </div>
        ) : (
          <>
            <Table data-testid="calls-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead></TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`calls-row-${c.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(c.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {c.direction === "inbound"
                        ? <ArrowDownLeft className="h-4 w-4 text-blue-500" />
                        : <ArrowUpRight className="h-4 w-4 text-emerald-500" />}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{formatPhone(c.contact_phone)}</TableCell>
                    <TableCell className="text-sm">{c.agent?.name || "—"}</TableCell>
                    <TableCell className="text-sm">{c.campaign?.name || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[c.status] || "outline"}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1">
                        {durationStr(c.result?.duration_s)}
                        {c.result?.recording_url && <Mic className="h-3.5 w-3.5 text-muted-foreground" />}
                      </span>
                    </TableCell>
                    <TableCell
                      className="text-sm font-mono tabular-nums"
                      data-testid={`calls-cost-${c.id}`}
                    >
                      {c.result?.cost_estimate_usd != null
                        ? `$${Number(c.result.cost_estimate_usd).toFixed(3)}`
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="ghost">
                        <Link to={`/calls/${c.id}`} data-testid={`calls-drill-${c.id}`}>
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > limit && (
              <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
                <span>Page {page + 1} of {Math.ceil(total / limit)}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <Button size="sm" variant="outline" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Filter({ label, value, options, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
